import { useMemo, useState } from "react";
import { Loader2, Save, Search, Sparkles } from "lucide-react";

const backendUrl = "https://www.sevaafoundation.com/seva-backend";

const categories = [
  "Child Education",
  "Tree Plantation",
  "Hunger Support",
  "Women Empowerment",
  "Homes for Old Age People",
  "Medical and Blood Donation",
];

type FormState = {
  title: string;
  category: string;
  short_description: string;
  content: string;
  seo_title: string;
  meta_description: string;
  seo_keywords: string;
  status: "draft" | "published";
};

const initialForm: FormState = {
  title: "",
  category: categories[0],
  short_description: "",
  content: "",
  seo_title: "",
  meta_description: "",
  seo_keywords: "",
  status: "published",
};

const AddBlog = () => {
  const [formData, setFormData] = useState<FormState>(initialForm);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  const seoPreviewTitle = useMemo(
    () => formData.seo_title.trim() || formData.title.trim() || "Your SEO title will appear here",
    [formData.seo_title, formData.title]
  );

  const seoPreviewDescription = useMemo(
    () =>
      formData.meta_description.trim() ||
      formData.short_description.trim() ||
      "Your meta description will appear here.",
    [formData.meta_description, formData.short_description]
  );

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setFormData((previous) => ({ ...previous, [field]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setMessage("");
    setIsError(false);

    const token = localStorage.getItem("sevaa_admin_token");
    const submitData = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      submitData.append(key, value);
    });

    try {
      const response = await fetch(`${backendUrl}/api/blogs/create.php`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: submitData,
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Blog could not be saved.");
      }

      setMessage(data.message || "Blog saved successfully.");
      setFormData(initialForm);
    } catch (error) {
      setIsError(true);
      setMessage(error instanceof Error ? error.message : "Error publishing blog.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="mb-7 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-orange-600">
            <Sparkles size={15} /> SEVAA Foundation Content
          </div>
          <h1 className="text-3xl font-black text-slate-950">Create New Blog</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
            Add useful NGO content with clear headings and accurate information. A common SEVAA Foundation cover image will be used automatically.
          </p>
        </div>
      </div>

      {message && (
        <div
          className={`mb-5 rounded-xl border p-4 text-sm font-semibold ${
            isError
              ? "border-red-200 bg-red-50 text-red-700"
              : "border-emerald-200 bg-emerald-50 text-emerald-700"
          }`}
        >
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <div className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div>
            <label className="mb-2 block text-sm font-bold text-slate-800">Blog Title</label>
            <input
              required
              type="text"
              value={formData.title}
              onChange={(event) => setField("title", event.target.value)}
              placeholder="Example: Why Equal Access to Education Matters for Every Child"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none transition focus:border-orange-500 focus:ring-4 focus:ring-orange-100"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-bold text-slate-800">SEVAA Foundation Category</label>
            <select
              value={formData.category}
              onChange={(event) => setField("category", event.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 outline-none transition focus:border-orange-500 focus:ring-4 focus:ring-orange-100"
            >
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between gap-4">
              <label className="text-sm font-bold text-slate-800">Short Description</label>
              <span className="text-xs font-semibold text-slate-400">
                {formData.short_description.length}/220
              </span>
            </div>
            <textarea
              required
              rows={3}
              maxLength={220}
              value={formData.short_description}
              onChange={(event) => setField("short_description", event.target.value)}
              placeholder="Write a clear 100–220 character summary of the article."
              className="w-full resize-y rounded-xl border border-slate-200 px-4 py-3 outline-none transition focus:border-orange-500 focus:ring-4 focus:ring-orange-100"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-bold text-slate-800">Full Content</label>
            <textarea
              required
              rows={18}
              value={formData.content}
              onChange={(event) => setField("content", event.target.value)}
              placeholder={`Use this structure:\n\nIntroduction paragraph\n\n## Main Heading\nUseful paragraph\n\n### Subheading\nUseful paragraph\n\n- First point\n- Second point`}
              className="w-full resize-y rounded-xl border border-slate-200 px-4 py-3 font-mono text-sm leading-7 outline-none transition focus:border-orange-500 focus:ring-4 focus:ring-orange-100"
            />
            <p className="mt-2 text-xs leading-5 text-slate-500">
              Use <strong>##</strong> for H2 headings, <strong>###</strong> for H3 headings and <strong>-</strong> for bullet points. These become semantic HTML on the public blog page.
            </p>
          </div>
        </div>

        <aside className="space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-5 flex items-center gap-2">
              <Search size={18} className="text-orange-600" />
              <h2 className="font-black text-slate-900">SEO Settings</h2>
            </div>

            <div className="space-y-5">
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-sm font-bold text-slate-700">SEO Title</label>
                  <span className={`text-xs font-bold ${formData.seo_title.length > 70 ? "text-red-500" : "text-slate-400"}`}>
                    {formData.seo_title.length}/70
                  </span>
                </div>
                <input
                  type="text"
                  maxLength={70}
                  value={formData.seo_title}
                  onChange={(event) => setField("seo_title", event.target.value)}
                  placeholder="Leave blank to use blog title"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-orange-500"
                />
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-sm font-bold text-slate-700">Meta Description</label>
                  <span className={`text-xs font-bold ${formData.meta_description.length > 160 ? "text-red-500" : "text-slate-400"}`}>
                    {formData.meta_description.length}/160
                  </span>
                </div>
                <textarea
                  rows={4}
                  maxLength={160}
                  value={formData.meta_description}
                  onChange={(event) => setField("meta_description", event.target.value)}
                  placeholder="120–160 characters describing the page accurately"
                  className="w-full resize-none rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-orange-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-bold text-slate-700">Topic Phrases</label>
                <input
                  type="text"
                  value={formData.seo_keywords}
                  onChange={(event) => setField("seo_keywords", event.target.value)}
                  placeholder="child education NGO, education support..."
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-orange-500"
                />
                <p className="mt-2 text-xs leading-5 text-slate-400">
                  Keep phrases natural. Do not repeat them unnaturally in the article.
                </p>
              </div>

              <div>
                <label className="mb-2 block text-sm font-bold text-slate-700">Publishing Status</label>
                <select
                  value={formData.status}
                  onChange={(event) => setField("status", event.target.value as FormState["status"])}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-orange-500"
                >
                  <option value="published">Publish Now</option>
                  <option value="draft">Save as Draft</option>
                </select>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="mb-1 text-xs text-slate-500">Google-style preview</p>
            <p className="break-words text-lg font-medium leading-6 text-blue-700">{seoPreviewTitle}</p>
            <p className="mt-1 text-sm text-emerald-700">www.sevaafoundation.com › blog</p>
            <p className="mt-1 text-sm leading-5 text-slate-600">{seoPreviewDescription}</p>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-950 px-6 py-3.5 font-bold text-white shadow-lg transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isLoading ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}
            {formData.status === "published" ? "Publish Blog" : "Save Draft"}
          </button>
        </aside>
      </form>
    </div>
  );
};

export default AddBlog;
